<script lang="ts" setup>
import { onMounted, reactive, ref } from 'vue'
import { useFeatureFlagsStore } from '@/stores'
import { storeToRefs } from 'pinia'
import FeatureTable from './FeatureTable.vue'
import FeatureEdit from './FeatureEdit.vue'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import type { FeatureFlagType } from '@/types/featureFlags'

const featureFlagsStore = useFeatureFlagsStore()
const {
  adminLoading,
  adminFlagCount,
  adminEnabledCount,
  adminDisabledCount,
  adminCategories
} = storeToRefs(featureFlagsStore)

const featureTableRef = ref<InstanceType<typeof FeatureTable> | null>(null)
const searchQuery = ref('')

const data = reactive({
  editMode: 'create' as 'create' | 'edit',
  editFlag: null as FeatureFlagType | null
})

const handleEdit = (flag: FeatureFlagType) => {
  data.editMode = 'edit'
  data.editFlag = { ...flag }
}

const handleCreate = () => {
  data.editMode = 'create'
  data.editFlag = { category: '', name: '', enabled: false }
}

const handleCloseEdit = () => {
  data.editFlag = null
}

const handleSaved = () => {
  featureFlagsStore.fetchAdminFlags()
}

onMounted(() => {
  featureFlagsStore.fetchAdminFlags()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.features.title')"
    :subtitle="$t('dashboard.admin.features.subtitle')"
    icon="mdi-toggle-switch-outline"
    icon-color="amber"
  >
    <template #actions>
      <button class="create-btn dashboard-action-btn dashboard-action-btn--primary" @click="handleCreate">
        <v-icon icon="mdi-plus" size="18" />
        <span>{{ $t('dashboard.admin.features.newFlag') }}</span>
      </button>
    </template>

    <!-- Stats Pills & Controls -->
    <div class="stats-container">
      <div class="stats-pills">
        <div class="stat-pill">
          <v-icon icon="mdi-flag-variant" size="16" />
          <span class="stat-value">{{ adminFlagCount }}</span>
          <span class="stat-label">{{ $t('dashboard.admin.features.stats.flags') }}</span>
        </div>
        <div class="stat-pill stat-enabled">
          <v-icon icon="mdi-check-circle" size="16" />
          <span class="stat-value">{{ adminEnabledCount }}</span>
          <span class="stat-label">{{ $t('dashboard.admin.features.stats.active') }}</span>
        </div>
        <div class="stat-pill stat-disabled">
          <v-icon icon="mdi-close-circle" size="16" />
          <span class="stat-value">{{ adminDisabledCount }}</span>
          <span class="stat-label">{{ $t('dashboard.admin.features.stats.inactive') }}</span>
        </div>
        <div class="stat-pill stat-categories">
          <v-icon icon="mdi-folder-outline" size="16" />
          <span class="stat-value">{{ adminCategories.length }}</span>
          <span class="stat-label">{{ $t('dashboard.admin.features.stats.categories') }}</span>
        </div>
      </div>

      <div class="stats-actions">
        <div class="search-box">
          <v-icon icon="mdi-magnify" size="18" />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('dashboard.admin.features.table.search')"
            class="search-input"
            @input="featureTableRef && (featureTableRef.search = searchQuery)"
          />
          <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''; featureTableRef && (featureTableRef.search = '')">
            <v-icon icon="mdi-close" size="16" />
          </button>
        </div>

        <button class="expand-all-btn" @click="featureTableRef?.expandAllCategories()">
          <v-icon icon="mdi-unfold-more-horizontal" size="18" />
          <span>{{ $t('dashboard.admin.features.table.expandAll') }}</span>
        </button>
      </div>
    </div>

    <!-- Feature Table -->
    <FeatureTable
      ref="featureTableRef"
      :loading="adminLoading"
      @edit="handleEdit"
      @refresh="featureFlagsStore.fetchAdminFlags"
    />

    <!-- Edit Panel -->
    <FeatureEdit
      :flag="data.editFlag"
      :mode="data.editMode"
      @close="handleCloseEdit"
      @saved="handleSaved"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.create-btn {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
  padding: var(--dashboard-btn-padding);
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.25);
  border-radius: var(--dashboard-btn-radius);
  color: #4caf50;
  font-size: var(--dashboard-subtitle-size);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.15);
    border-color: rgba(76, 175, 80, 0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

/* Stats Pills */
.stats-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.stats-pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--dashboard-gap-sm);
}

.stats-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  min-width: 200px;

  .v-icon {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus-within {
    border-color: rgba(156, 39, 176, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: rgba(200, 218, 255, 0.4);
  transition: color 0.2s ease;

  &:hover {
    color: rgba(200, 218, 255, 0.8);
  }
}

.expand-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(156, 39, 176, 0.08);
  border: 1px solid rgba(156, 39, 176, 0.2);
  border-radius: 10px;
  color: #ba68c8;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(156, 39, 176, 0.12);
    border-color: rgba(156, 39, 176, 0.3);
  }
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
  padding: 8px 12px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 24px;
  color: rgba(200, 218, 255, 0.7);

  .v-icon {
    color: rgba(200, 218, 255, 0.5);
  }

  .stat-value {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  .stat-label {
    font-size: var(--dashboard-stat-label-size);
    color: rgba(200, 218, 255, 0.5);
  }

  &.stat-enabled {
    background: rgba(76, 175, 80, 0.08);
    border-color: rgba(76, 175, 80, 0.2);

    .v-icon {
      color: #81c784;
    }

    .stat-value {
      color: #81c784;
    }
  }

  &.stat-disabled {
    background: rgba(244, 67, 54, 0.08);
    border-color: rgba(244, 67, 54, 0.2);

    .v-icon {
      color: #e57373;
    }

    .stat-value {
      color: #e57373;
    }
  }

  &.stat-categories {
    background: rgba(156, 39, 176, 0.08);
    border-color: rgba(156, 39, 176, 0.2);

    .v-icon {
      color: #ba68c8;
    }

    .stat-value {
      color: #ba68c8;
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .stat-pill {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);

    .stat-value {
      color: rgba(15, 23, 42, 0.9);
    }

    .stat-label {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .create-btn {
    justify-content: center;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .stats-container {
    flex-direction: column;
    align-items: stretch;
  }

  .stats-pills {
    justify-content: center;
  }

  .stats-actions {
    flex-direction: column;
  }

  .search-box {
    min-width: 100%;
  }

  .expand-all-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .create-btn {
    transition: none;

    &:hover {
      transform: none;
    }
  }
}
</style>
